import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  Heading,
  Input,
  SimpleGrid,
  Text
} from '@chakra-ui/react';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormError } from 'components/onboarding/form/FormError';
import { useTokenList } from 'hooks/query/useTokenList';
import { ProgramToken } from 'types';
import { usePoolBalance } from 'hooks/query/usePoolBalanceForm';
import { useTransactions } from 'store/transactions/useTransactions';
import { IRescueFromRewardPool } from 'components/onboarding/deployment/type';
import { ethers } from 'ethers';
import SelectTokenInput from 'components/onboarding/deployment/selectTokens/selectTokenInput';
import {
  mapPowerSwitchStatusToDescription,
  PowerSwitchStatus
} from 'api/blockchain/rewardProgram';

type Props = {
  aludelAddress: string;
  rewardPoolAddress: string;
  aludelAbi: ethers.ContractInterface;
  programState?: PowerSwitchStatus;
};

export const RescueTokensForm: FC<Props> = ({
  aludelAddress,
  rewardPoolAddress,
  aludelAbi,
  programState
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useFormContext<IRescueFromRewardPool>();

  const [selectedToken, setSelectedToken] = useState<ProgramToken>();
  const { data: tokenList = [] } = useTokenList();
  const { rescueTokensFromRewardPool } = useTransactions();
  const { insufficientBalance } = usePoolBalance(
    'amount',
    rewardPoolAddress,
    selectedToken
  );

  const handleRescueToken = async (d: IRescueFromRewardPool) => {
    if (selectedToken) {
      await rescueTokensFromRewardPool(
        aludelAddress,
        aludelAbi,
        selectedToken.address,
        selectedToken.tokenSymbol,
        d.recipient,
        d.amount,
        selectedToken.decimals
      );
    }
  };

  return (
    <>
      <Heading size='md' mb={6}>
        Rescue Tokens
      </Heading>
      <Text color='gray.200' mb={6}>
        You can use this option to withdraw tokens from the reward program pool
        while it is online.
      </Text>
      <Text color='gray.200' mb={6}>
        This only works for token addresses that are not registered to the
        Aludel (main reward or bonus tokens). If you need to withdraw tokens
        that are currently registered to the program, you must permanently
        shutdown the program first and use the rescue function in the Power
        Controller section.
      </Text>
      <SimpleGrid columns={[1, 1, 3, 4]} gap={4}>
        <GridItem>
          <SelectTokenInput
            tokenList={tokenList}
            setSelectedToken={setSelectedToken}
            required
          />
        </GridItem>

        <GridItem>
          <FormControl
            isRequired
            isInvalid={!!errors.amount}
            isDisabled={!selectedToken}
          >
            <FormLabel>Enter amount</FormLabel>
            <Input
              id='amount'
              type='number'
              borderColor={'gray.300'}
              placeholder='0'
              {...register('amount')}
            />
            {errors.amount && <FormError>{errors.amount.message}</FormError>}
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired isInvalid={!!errors.recipient}>
            <FormLabel>Enter Address</FormLabel>
            <Input
              id='address'
              type='text'
              borderColor={'gray.300'}
              placeholder='0x'
              {...register('recipient')}
            />
            {errors.recipient && (
              <FormError>{errors.recipient.message}</FormError>
            )}
          </FormControl>
        </GridItem>

        <GridItem>
          <Flex height='100%' alignItems='flex-end'>
            <Button
              onClick={handleSubmit((d) => handleRescueToken(d))}
              borderRadius='lg'
              disabled={
                insufficientBalance ||
                programState === PowerSwitchStatus.OFFLINE ||
                programState === PowerSwitchStatus.SHUTDOWN
              }
            >
              {programState === PowerSwitchStatus.OFFLINE ||
              programState === PowerSwitchStatus.SHUTDOWN
                ? mapPowerSwitchStatusToDescription[programState]
                : 'Rescue'}
            </Button>
          </Flex>
        </GridItem>
      </SimpleGrid>
    </>
  );
};
